/**
 * Taxonomy migration modal — per docs/specs/taxonomy.md §7.
 *
 * Collects target + migration scope and runs a bounded worker-side dry-run
 * preview before execution. Supports blocked/ready validation for retro-all scope
 * and surfaces rollout readiness state before enabling apply.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v2
 */
import { useEffect, useState } from 'react';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import type { TaxonomyMigrationScope } from '../../lib/types';
import { apiPost, getHttpErrorMessage } from '../../lib/http';

export type TaxonomyTargetKind = 'stage' | 'workflow' | 'tag';

export interface TaxonomyMigrationTarget {
    kind: TaxonomyTargetKind;
    name: string;
    change: string;
}

export interface TaxonomyMigrationApplyPayload {
    kind: TaxonomyTargetKind;
    target: string;
    change: string;
    scope: TaxonomyMigrationScope;
    reason?: string;
    two_factor_code?: string;
}

interface TaxonomyMigrationPreview {
    can_apply: boolean;
    affected_users: number;
    requires_two_fa: boolean;
    rollback_available: boolean;
    blockers: string[];
    warnings: string[];
    scope: TaxonomyMigrationScope;
    message: string;
}

type RolloutState = 'idle' | 'ready' | 'loading' | 'error';

const DEBOUNCE_MS = 260;
const RETRO_ALL_MIN_REASON = 8;

function joinItems(items: string[]) {
    return items.length ? items.join(' · ') : 'None';
}

function ScopeOption({
    id,
    scope,
    setScope,
    title,
    description
}: {
    id: TaxonomyMigrationScope;
    scope: TaxonomyMigrationScope;
    setScope: (s: TaxonomyMigrationScope) => void;
    title: string;
    description: string;
}) {
    const checked = scope === id;
    return (
        <label
            style={{
                display: 'grid',
                gridTemplateColumns: '18px 1fr',
                gap: 10,
                padding: '10px 12px',
                border: `1px solid ${checked ? 'var(--border-focus)' : 'var(--border)'}`,
                borderRadius: 8,
                cursor: 'pointer',
                background: checked ? 'var(--primary-surface)' : 'transparent'
            }}
        >
            <input
                type="radio"
                name="tax-scope"
                checked={checked}
                onChange={() => setScope(id)}
                style={{ marginTop: 2 }}
            />
            <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {description}
                </div>
            </div>
        </label>
    );
}

export function TaxonomyMigrationModal({
    target,
    onClose,
    onApply
}: {
    target: TaxonomyMigrationTarget | null;
    onClose: () => void;
    onApply: (payload: TaxonomyMigrationApplyPayload) => Promise<void>;
}) {
    const [scope, setScope] = useState<TaxonomyMigrationScope>('new_users_only');
    const [reason, setReason] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [state, setState] = useState<RolloutState>('idle');
    const [preview, setPreview] = useState<TaxonomyMigrationPreview | null>(null);
    const [error, setError] = useState('');
    const [applying, setApplying] = useState(false);

    const canApply =
        state === 'ready' && preview?.can_apply === true && preview.blockers.length === 0;

    const handleClose = () => {
        setScope('new_users_only');
        setReason('');
        setTwoFactorCode('');
        setState('idle');
        setPreview(null);
        setError('');
        setApplying(false);
        onClose();
    };

    useEffect(() => {
        if (!target) return;

        setScope('new_users_only');
        setReason('');
        setTwoFactorCode('');
        setState('idle');
        setError('');
        setPreview(null);
        setApplying(false);
    }, [target]);

    useEffect(() => {
        if (!target) return;

        if (
            scope === 'retro_all' &&
            (reason.trim().length < RETRO_ALL_MIN_REASON || !/^\d{6}$/.test(twoFactorCode.trim()))
        ) {
            setState('ready');
            setError('');
            setPreview({
                can_apply: false,
                affected_users: 0,
                requires_two_fa: true,
                rollback_available: false,
                blockers: [
                    reason.trim().length < RETRO_ALL_MIN_REASON
                        ? 'Retro-all requires a reason before dry-run validation can run.'
                        : 'Retro-all requires a 6 digit 2FA proof code.'
                ],
                warnings: ['Retro-all is owner-gated and records a rollback receipt.'],
                scope,
                message:
                    reason.trim().length < RETRO_ALL_MIN_REASON
                        ? `Add a reason (${RETRO_ALL_MIN_REASON - reason.trim().length} more chars needed)`
                        : 'Enter the 6 digit 2FA proof code'
            });
            return;
        }

        setState('loading');
        const timer = setTimeout(async () => {
            try {
                const result = await apiPost<TaxonomyMigrationPreview>(
                    '/api/admin/taxonomy/migrations/preview',
                    {
                        kind: target.kind,
                        target: target.name,
                        change: target.change,
                        scope,
                        reason: scope === 'retro_all' ? reason.trim() : undefined,
                        two_factor_code: scope === 'retro_all' ? twoFactorCode.trim() : undefined
                    }
                );

                setPreview(result);
                setState('ready');
            } catch (nextError) {
                setError(getHttpErrorMessage(nextError, 'Dry-run preview failed'));
                setPreview(null);
                setState('error');
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [target, scope, reason, twoFactorCode]);

    const handleApply = async () => {
        if (!target || !canApply) return;

        setApplying(true);
        setError('');
        try {
            await onApply({
                kind: target.kind,
                target: target.name,
                change: target.change,
                scope,
                reason: scope === 'retro_all' ? reason.trim() : undefined,
                two_factor_code: scope === 'retro_all' ? twoFactorCode.trim() : undefined
            });
            handleClose();
        } catch (nextError) {
            setError(getHttpErrorMessage(nextError, 'Migration could not be queued'));
        } finally {
            setApplying(false);
        }
    };

    if (!target) return null;

    const applyDisabledReason = applying
        ? 'Submitting migration request…'
        : state === 'loading'
          ? 'Validating scope on worker…'
          : !preview
            ? 'Waiting for validation'
            : preview.blockers.length
              ? joinItems(preview.blockers)
              : canApply
                ? 'Ready to queue'
                : 'Execution is preview-only; rollout jobs are not wired yet.';

    return (
        <Modal
            open={!!target}
            onClose={handleClose}
            title={`Apply ${target.kind} change`}
            subtitle={target.change}
            width={620}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={handleClose} disabled={applying}>
                        Close
                    </button>
                    <RoleButton
                        scope={scope === 'retro_all' ? 'owner_only' : 'support_ok'}
                        className="btn btn-primary"
                        onClick={handleApply}
                        disabled={applying || !canApply}
                        title={applyDisabledReason}
                    >
                        {applying ? 'Applying…' : 'Apply migration'}
                    </RoleButton>
                </>
            }
        >
            <div className="form-field">
                <span className="form-label">Target</span>
                <div
                    style={{
                        padding: 10,
                        background: 'var(--bg-input)',
                        borderRadius: 6,
                        border: '1px solid var(--border)',
                        fontSize: 12,
                        color: 'var(--text-primary)'
                    }}
                >
                    {target.kind} · <strong>{target.name}</strong>
                </div>
            </div>

            <div className="form-field">
                <span className="form-label">Migration scope</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <ScopeOption
                        id="new_users_only"
                        scope={scope}
                        setScope={setScope}
                        title="New users only"
                        description="Safe default — existing users keep their current defaults."
                    />
                    <ScopeOption
                        id="retro_untouched"
                        scope={scope}
                        setScope={setScope}
                        title="Retro: apply to users who haven't customised"
                        description="Only users that still match default taxonomy state are included."
                    />
                    <ScopeOption
                        id="retro_all"
                        scope={scope}
                        setScope={setScope}
                        title="Retro: apply to all users"
                        description="Overrides per-user customisations. Requires owner + validated reason."
                    />
                </div>
            </div>

            <div
                style={{
                    padding: 10,
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    background: 'var(--bg-input)',
                    fontSize: 12,
                    color: 'var(--text-primary)',
                    marginBottom: 12
                }}
            >
                {state === 'loading' ? (
                    <>Recomputing scope preview on the worker…</>
                ) : state === 'error' ? (
                    <span className="text-danger">Preview failed: {error}</span>
                ) : preview ? (
                    <>
                        Preview: <strong>{preview.affected_users.toLocaleString()}</strong> users
                        would be affected.
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            {preview.message}
                        </div>
                    </>
                ) : (
                    'Waiting for scope validation.'
                )}
            </div>

            {scope === 'retro_all' && (
                <div className="form-field" style={{ marginTop: 12 }}>
                    <label className="form-label" htmlFor="reason">
                        Reason (required for retro-all)
                    </label>
                    <textarea
                        id="reason"
                        className="form-textarea"
                        style={{ minHeight: 80 }}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. New taxonomic defaults for sync brief rollout · legal approved #PD-2512"
                    />
                    <div className="form-hint">
                        Required length: {Math.max(RETRO_ALL_MIN_REASON - reason.trim().length, 0)}{' '}
                        / {RETRO_ALL_MIN_REASON} characters.
                    </div>
                    <label className="form-label" htmlFor="two-factor-code">
                        2FA proof code
                    </label>
                    <input
                        id="two-factor-code"
                        className="form-input"
                        inputMode="numeric"
                        maxLength={6}
                        value={twoFactorCode}
                        onChange={(e) =>
                            setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                        }
                        placeholder="123456"
                    />
                </div>
            )}

            <div className="form-field" style={{ marginTop: 12 }}>
                <span className="form-label">Rollout readiness</span>
                <div
                    style={{
                        padding: 10,
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        background: 'var(--bg-input)',
                        fontSize: 12,
                        color: 'var(--text-primary)'
                    }}
                >
                    {preview ? (
                        <>
                            Validation: <strong>{preview.can_apply ? 'ready' : 'blocked'}</strong>
                            <div className="form-hint">Blocked: {joinItems(preview.blockers)}</div>
                            <div className="form-hint">Warnings: {joinItems(preview.warnings)}</div>
                            <div className="form-hint">
                                Rollback token available:{' '}
                                {preview.rollback_available ? 'yes' : 'not yet'}
                            </div>
                            {preview.requires_two_fa && (
                                <div className="form-hint">
                                    Retro-all execution remains bound to 2FA and rollout service.
                                </div>
                            )}
                        </>
                    ) : (
                        'Awaiting worker validation.'
                    )}
                </div>
            </div>

            {error && state === 'error' && (
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
            )}
        </Modal>
    );
}
